import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DateTime } from 'luxon';
import ReactPagination from 'react-js-pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';

// actions
import formatDispositionNumToString from '../../utils/format-disposition';

// components
import { Table } from '../../common/components';

function Pagination({ handleLoadShoppers }) {
  const dispatch = useDispatch();
  const {
    activePage,
    pageSize,
    totalPages,
    totalLeads,
    leadBook,
  } = useSelector(store => store.leads);

  const pageCountStart = ((activePage - 1) * pageSize) + 1;
  let pageCountEnd = activePage * pageSize;
  if (totalPages === activePage) pageCountEnd = totalLeads;

  return (
    <div className="d-flex flex-row m-3 position-relative">
      <div className="d-flex ms-0 position-absolute">
        <span className="text-muted">
          <i>{`${pageCountStart}-${pageCountEnd} of ${totalLeads} leads`}</i>
        </span>
      </div>
      <ReactPagination
        activePage={activePage}
        itemsCountPerPage={pageSize}
        totalItemsCount={totalLeads}
        pageRangeDisplayed={5}
        innerClass="pagination mb-0 ms-auto me-auto"
        itemClass="page-item"
        linkClass="page-link"
        onChange={(pageNum) => {
          if (leadBook[pageNum - 1]) {
            dispatch({ type: 'SET_LEADS_KEYS', payload: { activePage: pageNum } });
          } else {
            handleLoadShoppers({ page: pageNum });
          }
        }}
      />
    </div>
  );
}

function UserTable({ handleLoadShoppers, loading, errorMessage }) {
  const dispatch = useDispatch();
  const {
    theme: {
      features: { leadReassignment: leadReassignmentFeature },
      selectedLeadsMax = 10,
    },
    leads: {
      leadPage: leads,
      sortInverse: sortDirection,
      sortTerm,
      agentColumnDisplay = 'name',
      selectedLeads = [],
    },
  } = useSelector(store => store);

  // cell formatting
  const toDateString = (date) => DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
  const formatDateCell = (date) => (date ? toDateString(date) : <span className="text-muted">--</span>);
  const formatNotSetCell = (text) => (text || <i className="text-muted">Not Set</i>);

  function formatAgentCell(data) {
    const agentDataMissing = !data.agent_username;
    const openAgent = (
      <FontAwesomeIcon
        className="ms-2"
        role="button"
        icon={faArrowUpRightFromSquare}
        disabled={!data.agent_username}
        onClick={() => window.open(`/admin/${data.agent_username}`, '_blank')}
      />
    );

    let text = '--';
    if (agentColumnDisplay === 'name') {
      text = `${data.agent_first_name || ''}${data.agent_last_name ? ` ${data.agent_last_name}` : ''}`;
    }
    if (agentColumnDisplay === 'email') text = data.agent_email;
    if (agentColumnDisplay === 'npn') text = data.npn;
    if (agentDataMissing) text = <i>No Agent Data</i>;
    return (
      <span>{text}{!agentDataMissing && openAgent}</span>
    );
  }

  function formatReassignCell(data) {
    const checked = selectedLeads.find(l => (l.username === data.username));
    // console.log('username: ', data.username, ' | checked: ', checked);
    const isDisabled = ((selectedLeads.length >= selectedLeadsMax) && !checked);

    return (
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={checked}
          disabled={isDisabled}
          onChange={() => {
            if (checked) dispatch({ type: 'REMOVE_SELECTED_LEAD', username: data.username });
            if (!checked) dispatch({ type: 'ADD_SELECTED_LEAD', lead: data });
          }}
        />
      </div>
    );
  }

  function formatDetailsCell(data) {
    return (
      <button
        type="button"
        className="btn btn-sm btn-link p-0"
        onClick={() => {
          dispatch({ type: 'SET_LEADS_KEYS', payload: { leadDetails: data } });
          dispatch({ type: 'OPEN_MODAL', modal: 'leadDetailsModal' });
        }}
      >
        Details
      </button>
    );
  }

  function rowClassHandler(data) {
    const currentlySelected = selectedLeads.find(l => (l.username === data.username));
    if (currentlySelected) return 'table-primary';
    return '';
  }

  let agentColumnHeader = '';
  if (agentColumnDisplay === 'name') agentColumnHeader = 'Agent Name';
  if (agentColumnDisplay === 'email') agentColumnHeader = 'Agent Email';
  if (agentColumnDisplay === 'npn') agentColumnHeader = 'Agent NPN';

  // table schema
  const schema = {
    cols: [
      (leadReassignmentFeature ? {
        header: '',
        passAllData: true,
        formatter: formatReassignCell,
      } : undefined),
      {
        header: agentColumnHeader,
        passAllData: true,
        formatter: formatAgentCell,
        sortKey: `agent_${agentColumnDisplay}`,
        alphaSortIcon: true,
        defaultSortOrder: 'asc',
      },
      {
        header: 'Lead ID',
        field: 'lead_id',
        sortKey: 'lead_id',
        alphaSortIcon: true,
        defaultSortOrder: 'asc',
      },
      {
        header: 'Lead Type',
        field: 'lead_type',
        formatter: formatNotSetCell,
        sortKey: 'lead_type',
        alsphaSortIcon: true,
      },
      {
        header: 'Lead Source',
        field: 'lead_source',
        formatter: formatNotSetCell,
        sortKey: 'lead_source',
        alsphaSortIcon: true,
      },
      {
        header: 'Lead Status',
        field: 'lead_status',
        formatter: formatDispositionNumToString,
        sortKey: 'lead_status',
      },
      {
        header: 'Created',
        field: 'created',
        formatter: formatDateCell,
        sortKey: 'created',
      },
      {
        header: 'Details',
        passAllData: true,
        formatter: formatDetailsCell,
      },
    ],
    rowClassHandler,
    sortClickCallback: (key, direction) => handleLoadShoppers({ sortTerm: key, sortInverse: direction }),
    sortKeySetter: (sk) => dispatch({ type: 'SET_LEADS_KEYS', payload: { sortTerm: sk } }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_LEADS_KEYS', payload: { sortInverse: sd } }),
    sortDirectionGetter: () => sortDirection,
  };

  return (
    <>
      <Pagination handleLoadShoppers={handleLoadShoppers} />
      <div className="overflow-scroll">
        <Table
          className="table table-striped"
          schema={schema}
          data={leads}
          loading={loading}
          errorMessage={errorMessage}
        />
      </div>
      <Pagination handleLoadShoppers={handleLoadShoppers} />
    </>
  );
}

export default UserTable;
